import type { Story } from '@nelnela/contracts';
import type { QueueLine } from './tts-queue';

/**
 * The pure half of playing a scripted piece aloud (M6b step 6).
 *
 * The queue reducer itself is `lib/tts-queue.ts`, shared with Listen and unchanged. What a piece
 * adds is only this: which voice says which sentence. That mapping is worth holding still on its
 * own, because getting it wrong is not a visible bug — it is a two-hander read entirely in one
 * voice, which sounds like a slightly odd narrator rather than like a defect.
 */

/**
 * The player key namespace.
 *
 * `ttsPlayer` is shared app-wide — a gloss sheet's "▶ hear it" and Listen's scene both live on the
 * same single track — so every `onEnded` subscriber checks the key before acting on it. Listen
 * uses `listen:`; a piece uses `piece:` for the same reason and must not collide, or finishing a
 * gloss clip would step a piece forward.
 */
export const PIECE_KEY_PREFIX = 'piece:';
export const pieceKey = (storyId: string, idx: number): string =>
  `${PIECE_KEY_PREFIX}${storyId}:${idx}`;

export const isPieceKey = (key: string | null | undefined): boolean =>
  typeof key === 'string' && key.startsWith(PIECE_KEY_PREFIX);

/**
 * A piece's sentences as queue lines, each carrying the voice of whoever says it.
 *
 * `startMs`/`endMs` are 0 because a piece has no timeline — the same reason `storyLines` sets
 * them to 0 and the reader passes `gutter="none"`. They are on `QueueLine` for Listen, which
 * plays a span of a real transcript.
 *
 * A sentence whose speaker is not in `voices` gets NO voice rather than a wrong one: the server's
 * `voiceFor` then resolves the learner's own default, which is a coherent thing to hear. Sending
 * some other speaker's voice would be actively misleading, and publishing already refuses a piece
 * that could reach this state — this is the belt to that braces.
 */
export function pieceQueueLines(story: Pick<Story, 'id' | 'sentences' | 'voices'>): QueueLine[] {
  const voices = story.voices ?? {};
  return story.sentences.map((s) => {
    const voice = s.speaker ? voices[s.speaker] : undefined;
    return {
      lineId: pieceKey(story.id, s.idx),
      text: s.text,
      startMs: 0,
      endMs: 0,
      ...(voice ? { voice } : {}),
    };
  });
}

/**
 * How many distinct voices a piece will actually be heard in.
 *
 * The reader shows this ("two voices") and the play control uses it to decide whether to say so
 * at all. Counting the VOICES rather than the speakers is deliberate: two speakers mapped to one
 * voice is one voice, and telling the learner otherwise would be a promise the audio breaks.
 */
export function voiceCount(story: Pick<Story, 'sentences' | 'voices'>): number {
  const voices = story.voices ?? {};
  const heard = new Set<string>();
  for (const s of story.sentences) {
    const v = s.speaker ? voices[s.speaker] : undefined;
    if (v) heard.add(v);
  }
  return heard.size;
}

/** The speaker names in the order they first speak — the reader's "Nino · Giorgi" line. */
export function speakerOrder(story: Pick<Story, 'sentences'>): string[] {
  const seen: string[] = [];
  for (const s of story.sentences) {
    if (s.speaker && !seen.includes(s.speaker)) seen.push(s.speaker);
  }
  return seen;
}

/** Which sentence index a queue position refers to. `-1` when the queue has run off the end. */
export function sentenceAt(story: Pick<Story, 'sentences'>, idx: number): number {
  return story.sentences[idx]?.idx ?? -1;
}
